import React, { ReactElement, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { Modal, MODAL_ANIMATION_TYPE, MODAL_CLOSE_BUTTON, MODAL_TYPE } from 'b2b-components/theme/components/Modal';
import { useAddKanbanItemsToCartData, useKanbanContext, useVerifyKanbanItemsData } from 'b2b-apps/core/kanban';
import { KanbanOrderFormValuesType } from 'b2b-apps/core/kanban/components/KanbanOrder';
import {
  AddItemsToCartContent,
  AddItemsToExistingCartContent,
  ContentConfirm,
  VerifyOrderContent,
} from 'b2b-apps/shop/common/components';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useRouterPush, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { openLink } from 'b2b-apps/shop/routes/utils/url';
import { KANBAN_ORDER_MODAL_TYPE } from './KanbanModal.types';

export const KanbanModal = (): ReactElement => {
  const t = useTranslate();
  const push = useRouterPush();
  const urlCreator = useUrlCreator();

  const verifyData = useVerifyKanbanItemsData();
  const addItemsToCartData = useAddKanbanItemsToCartData();

  const {
    modalType,
    closeModal,
    handleFormClear,
    handleClearCartAndAddToCart,
    handleConfirmAddToCart,
  } = useKanbanContext();
  const { resetForm } = useFormikContext<KanbanOrderFormValuesType>();

  const handleGoToCart = () => {
    openLink(urlCreator(ROUTES.cart), push);
  };

  const renderContent = useCallback((): ReactElement | null => {
    switch (modalType) {
      case KANBAN_ORDER_MODAL_TYPE.addToCartConfirm:
        return (
            <AddItemsToExistingCartContent
              onClearAndAdd={handleClearCartAndAddToCart}
              onAdd={handleConfirmAddToCart}
              onClose={closeModal}
            />
        );
      case KANBAN_ORDER_MODAL_TYPE.addToCart:
        return (
            <AddItemsToCartContent
              onModalClose={closeModal}
              addItemsToCartData={addItemsToCartData}
              onModalConfirmClick={handleGoToCart}
            />
        );

      case KANBAN_ORDER_MODAL_TYPE.clearForm:
        return (
            <ContentConfirm
              onClose={closeModal}
              onConfirm={handleFormClear(resetForm)}
              text={{
                title: t('Confirm clearing the form'),
                description: t('All data entered in the form will be lost'),
                cancelLabel: t('Back'),
                confirmLabel: t('Clear form'),
              }}
            />
        );

      case KANBAN_ORDER_MODAL_TYPE.verify:
        return (
            <VerifyOrderContent
              onModalClose={closeModal}
              createVerifyData={verifyData}
            />
        );

      default:
        return null;
    }
  },
  [
    modalType,
    closeModal,
    addItemsToCartData,
    verifyData,
    handleFormClear,
  ]);

  return (
    <Modal
      animationType={MODAL_ANIMATION_TYPE.fade}
      closeButton={MODAL_CLOSE_BUTTON.right}
      content={renderContent}
      isOpen={!!modalType}
      onCloseModal={closeModal}
      text={{
        ariaLabel: t('Kanban Order'),
        ariaCloseModal: t('Close'),
      }}
      type={MODAL_TYPE.small}
    />
  );
};
